import { useEffect } from "react";
import type { PageNode } from "@baze/schema";
import { INVITATION_SCRIPT, LESSONS_SCRIPT, SLIDER_SCRIPT, needsInvitationScript, needsLessonsScript, needsResponsesScript, needsSliderScript, responsesScript } from "@baze/engine/runtime";

/**
 * Runs the live site's inline enhancers (sliders, lessons search, countdown and invitation opening, wishes/RSVP) on a
 * draft preview, so it behaves like the published page. Forms run in demo mode: nothing is sent. No
 * analytics. React never executes an inline <script> it renders, so each one is created here; the
 * scripts are safe to run again, and `runKey` (page, draft version) re-runs them on new DOM.
 */
export function SiteRuntime({ page, runKey }: { page: Pick<PageNode, "sections">; runKey: string }) {
  const slider = needsSliderScript(page);
  const invitation = needsInvitationScript(page);
  const responses = needsResponsesScript(page);
  const lessons = needsLessonsScript(page);
  useEffect(() => {
    const sources = [slider && SLIDER_SCRIPT, invitation && INVITATION_SCRIPT, responses && responsesScript("", true, true), lessons && LESSONS_SCRIPT].filter((s): s is string => !!s);
    const els = sources.map((text) => {
      const s = document.createElement("script");
      s.text = text;
      document.body.appendChild(s);
      return s;
    });
    return () => els.forEach((s) => s.remove());
  }, [runKey, slider, invitation, responses, lessons]);
  return null;
}
